import type { UnsubscribeRequest } from "@/modules/eventDetails/client/api/types"
import type { FunctionComponent } from "react"

import { useMemo, useState } from "react"

import { Link } from "@jobteaser/ui-kit/components/Link"
import { Modal } from "@jobteaser/ui-kit/components/Modal/Modal"

import { useRegistrationStatus } from "@/modules/eventDetails/client/hooks/useRegistrationStatus"
import { useUnsubscribe } from "@/modules/eventDetails/client/hooks/useUnsubscribe"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { useNotification } from "@/modules/notification/hooks"

import styles from "./Unsubscribe.module.css"

type UnsubscribeProps = {
  id: string
}

export const Unsubscribe: FunctionComponent<UnsubscribeProps> = ({ id }) => {
  const { t } = useTranslation(["event_details", "shared_modal", "shared_error"])
  const locale = useLocale()
  const showNotification = useNotification()
  const { refetch: refetchRegistrationStatus } = useRegistrationStatus({ eventId: id })
  const hostname = typeof window !== "undefined" ? window.location.host : ""
  const params = useMemo(
    () =>
      ({
        hostname,
        id,
        locale,
      } as UnsubscribeRequest),
    [id, locale, hostname]
  )

  const { mutate: getUnsubscribe } = useUnsubscribe()
  const [isUnsubscribeModalOpen, setIsUnsubscribeModalOpen] = useState(false)
  const [isButtonLoading, setIsButtonLoading] = useState(false)

  const handleConfirm = (): void => {
    // submit request then close modal
    setIsButtonLoading(true)
    getUnsubscribe(params, {
      onError: () => {
        showNotification({
          content: "",
          duration: 10000,
          title: t("shared_error.generic"),
          variant: "error",
        })
        setIsButtonLoading(false)
        setIsUnsubscribeModalOpen(false)
      },
      onSuccess: () => {
        refetchRegistrationStatus()
        showNotification({
          content: "",
          duration: 10000,
          title: t("event_details.notifications.unsubscribe.success.title"),
          variant: "success",
        })
        setIsButtonLoading(false)
        setIsUnsubscribeModalOpen(false)
      },
    })
  }

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <Link
        className={styles.link}
        isButton
        onClick={() => setIsUnsubscribeModalOpen(true)}
        data-testid="event-details-registration-details-unsubscribe-link"
      >
        {t("event_details.unsubscribe")}
      </Link>
      {isUnsubscribeModalOpen && (
        <Modal
          onClose={() => setIsUnsubscribeModalOpen(false)}
          isOpen
          wrapperId="jobteaser-uishift"
          data-testid="event-details-registration-details-unsubscribe-modal"
        >
          <Modal.Header data-testid="event-details-registration-details-unsubscribe-modal-header">
            <Modal.Title data-testid="event-details-registration-details-unsubscribe-modal-title">
              {t("event_details.unsubscription_modal.title")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body data-testid="event-details-registration-details-unsubscribe-modal-body">
            <p>{t("event_details.unsubscription_modal.content")}</p>
          </Modal.Body>
          <Modal.Footer data-testid="event-details-registration-details-unsubscribe-modal-footer">
            <Modal.Button
              onClick={handleConfirm}
              disabled={isButtonLoading}
              isLoading={isButtonLoading}
              data-testid="event-details-registration-details-unsubscribe-modal-confirm-button"
            >
              {t("shared_modal.confirm")}
            </Modal.Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  )
}
